import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '@accounts/config';
import { Resume, Return } from '@accounts/domain';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Loading } from '@beyond-js/ui/react';

/**
 * Where someone who has just signed in goes, reached as the sign-in address once they are in.
 *
 * A product that asked for them back (`?product=&return=`, as the Beyond Desktop's quick login does
 * when a person goes back to sign in with a password) gets them back once the service validates the
 * address; otherwise they go to the screen of this interface they were on their way to (`?to=`), which
 * `Resume` accepts only as a path of this application, or to the overview. A destination that is not
 * followed is said so on the overview, never followed.
 *
 * An interrupted hand-off (`?to=/v1/authorize?…`) is not restarted from here: the sign-in screen
 * resumes it the moment the person signs in, and this address is reached with a session only by going
 * back to it, when the product's challenge has already been spent. The one exception is a hand-off the
 * service could not answer (`?resume=1`, Accounts unavailable): no code was issued, so once Accounts
 * answers the person goes on to the product instead of stopping here. The page is replaced, so Back
 * never reaches this address again. In the window a page's session opened (`?mode=window`) a person
 * already signed in goes to the landing, which tells that page and closes the window.
 *
 * Its waiting line is a waiting element of the document's opening (`data-bui-opening-wait`, said in
 * the line's own words): when this is the first page loaded, the opening stays until the browser
 * leaves for the product or the screen the person goes to draws, so this page is never shown on its
 * own on the way. After the opening has ended, the marker changes nothing.
 */
export function Resumed() {
	const { arrive, language } = usePresence();
	const [parameters] = useSearchParams();
	const navigate = useNavigate();
	const started = useRef(false);

	useEffect(() => {
		if (started.current) return;
		started.current = true;
		const ignored = { replace: true, state: { ignored: true } };

		const landed = Resume.landed(parameters);
		if (landed) {
			globalThis.location.replace(landed);
			return;
		}

		if (Return.requested(parameters)) {
			void arrive(parameters).then(sender => (sender ? globalThis.location.replace(sender.destination) : navigate('/', ignored)));
			return;
		}

		const to = parameters.get('to');
		const interrupted = parameters.get(Resume.OUTAGE) === '1' ? Resume.handoff(to, config.authority) : undefined;
		if (interrupted) {
			globalThis.location.replace(interrupted);
			return;
		}
		const path = Resume.path(to);
		if (path) navigate(path, { replace: true });
		else if (parameters.get(Return.PRODUCT) || (to && !Resume.handoff(to, config.authority))) navigate('/', ignored);
		else navigate('/', { replace: true });
	}, [arrive, navigate, parameters]);

	return (
		<Screen>
			<div data-bui-opening-wait="">
				<Loading label={Loading.labels[language]} />
			</div>
		</Screen>
	);
}
